import { useState, useEffect } from "react";
import axios from "axios";
import { Mic, Save, Loader2, Settings, Clock, MessageSquare } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminVoiceSettings = ({ authToken, projectId = "factoryhype" }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    allow_user_choice: false,
    voice_mandatory: false,
    voice_mandatory_text: "Bevor es weitergeht, nimm bitte eine kurze persönliche Sprachnachricht auf. Erzähl uns in 2-3 Sätzen, warum du den Job gerne machen möchtest. Das hilft uns, dich besser kennenzulernen und gibt dir einen echten Vorteil gegenüber anderen Bewerbern.",
    max_duration_seconds: 30,
    prompt_text: "Kein Stress 🙂 einfach kurz drauf los sprechen"
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchSettings();
  }, [projectId]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/voice-settings/${projectId}`, getAuthHeaders());
      setSettings({
        enabled: response.data.enabled || false,
        allow_user_choice: response.data.allow_user_choice || false,
        voice_mandatory: response.data.voice_mandatory || false,
        voice_mandatory_text: response.data.voice_mandatory_text || "Bevor es weitergeht, nimm bitte eine kurze persönliche Sprachnachricht auf. Erzähl uns in 2-3 Sätzen, warum du den Job gerne machen möchtest. Das hilft uns, dich besser kennenzulernen und gibt dir einen echten Vorteil gegenüber anderen Bewerbern.",
        max_duration_seconds: response.data.max_duration_seconds || 30,
        prompt_text: response.data.prompt_text || "Kein Stress 🙂 einfach kurz drauf los sprechen"
      });
    } catch (error) {
      console.error("Error fetching voice settings:", error);
      toast.error("Fehler beim Laden der Voice-Einstellungen");
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/voice-settings/${projectId}`, settings, getAuthHeaders());
      toast.success("Voice-Einstellungen gespeichert!");
    } catch (error) {
      console.error("Error saving voice settings:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voice-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Mic className="w-6 h-6 text-purple-600" />
            Voice vor Kontaktformular
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Sprachnachricht vor dem Kontaktformular aktivieren/deaktivieren
          </p>
        </div>
        <Button onClick={saveSettings} disabled={isSaving} data-testid="save-voice-settings">
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Speichern
        </Button>
      </div>

      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-500" />
            Grundeinstellungen
          </CardTitle>
          <CardDescription>
            Aktiviere oder deaktiviere die Sprachaufnahme vor dem Kontaktformular
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">Voice-Aufnahme aktivieren</Label>
              <p className="text-sm text-slate-500">
                Wenn aktiv, müssen Bewerber vor dem Kontaktformular eine kurze Sprachnachricht aufnehmen
              </p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
              data-testid="voice-enabled-toggle"
            />
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Aktueller Status:</span>
            {settings.enabled ? (
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Aktiv
              </span>
            ) : (
              <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">
                Inaktiv
              </span>
            )}
          </div>
          
          {/* User Choice Option - Only visible when Voice is enabled */}
          {settings.enabled && (
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="space-y-1">
                <Label className="text-base font-medium">Bewerber darf wählen</Label>
                <p className="text-sm text-slate-500">
                  Bewerber kann zwischen Sprachnachricht oder direktem Kontaktformular wählen
                </p>
                <div className="mt-2 text-xs text-blue-600 bg-blue-100 p-2 rounded">
                  <strong>Auswahl-Buttons:</strong><br/>
                  🎤 Kurz Sprachnachricht senden<br/>
                  ⌨️ Lieber direkt Kontaktdaten eintragen
                </div>
              </div>
              <Switch
                checked={settings.allow_user_choice}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, allow_user_choice: checked, voice_mandatory: checked ? false : prev.voice_mandatory }))}
                data-testid="voice-user-choice-toggle"
              />
            </div>
          )}
          {settings.enabled && (
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="space-y-1">
                <Label className="text-base font-medium">Voice-Aufnahme Pflicht</Label>
                <p className="text-sm text-slate-500">
                  Bewerber MUSS eine Sprachnachricht aufnehmen – kein Überspringen möglich
                </p>
                <p className="text-xs text-red-500 mt-1">
                  Ein motivierender Text wird vorher angezeigt
                </p>
              </div>
              <Switch
                checked={settings.voice_mandatory}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, voice_mandatory: checked, allow_user_choice: checked ? false : prev.allow_user_choice }))}
                data-testid="voice-mandatory-toggle"
              />
            </div>
          )}
          {settings.enabled && settings.voice_mandatory && (
            <div className="p-4 bg-red-50/50 rounded-lg border border-red-100">
              <Label className="text-sm font-medium text-red-700 mb-2 block">Motivationstext (wird vor der Aufnahme angezeigt)</Label>
              <textarea
                value={settings.voice_mandatory_text}
                onChange={(e) => setSettings(prev => ({ ...prev, voice_mandatory_text: e.target.value }))}
                rows={4}
                className="w-full rounded-lg border border-red-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="Bevor es weitergeht, nimm bitte eine kurze persönliche Sprachnachricht auf..."
                data-testid="voice-mandatory-text"
              />
              <p className="text-xs text-slate-400 mt-1">Tipp: Kurz, motivierend, persönlich – der Bewerber soll sich wohlfühlen</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duration Settings */}
      <Card className={!settings.enabled ? "opacity-50" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-500" />
            Aufnahme-Dauer
          </CardTitle>
          <CardDescription>
            Maximale Länge der Sprachaufnahme in Sekunden
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min="10"
                max="120"
                value={settings.max_duration_seconds}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  max_duration_seconds: parseInt(e.target.value) || 30 
                }))}
                disabled={!settings.enabled}
                className="w-24"
                data-testid="voice-max-duration"
              />
              <span className="text-slate-600">Sekunden</span>
            </div>
            <p className="text-xs text-slate-500">
              Empfohlen: 20-30 Sekunden für eine kurze Vorstellung
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Prompt Text Settings */}
      <Card className={!settings.enabled ? "opacity-50" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-slate-500" />
            Aufforderungstext
          </CardTitle>
          <CardDescription>
            Dieser Text wird dem Bewerber vor der Aufnahme angezeigt
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={settings.prompt_text}
            onChange={(e) => setSettings(prev => ({ ...prev, prompt_text: e.target.value }))}
            disabled={!settings.enabled}
            rows={4}
            placeholder="Text eingeben..."
            className="resize-none"
            data-testid="voice-prompt-text"
          />
          <p className="text-xs text-slate-500 mt-2">
            Tipp: Halte den Text kurz und motivierend
          </p>
        </CardContent>
      </Card>

      {/* Preview */}
      {settings.enabled && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="text-purple-800">Vorschau im Chat</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-slate-700 mb-4">{settings.prompt_text}</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Mic className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-sm text-slate-500">
                  Aufnahme starten (max. {settings.max_duration_seconds} Sek.)
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminVoiceSettings;
